import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Fade } from '@material-ui/core';
import ToggleButtonSwitch from 'components/shared/toggleButtonSwitch';
import { getToggleReportViewItems } from 'utility/reportUtils';
import Search from '../../search';
import CustomButton from '../../customButton';
import UserAvatar from '../../userAvatar';
import Scrollbars from '../../scrollbars';
import ConditionalTooltip from '../../conditionalTooltip';
import { ReactComponent as RemoveIcon } from '../../../../assets/icons/close-gray.svg';
import { getPersonShortName, isArrayEmpty } from '../../../../utility/helpers';
import {
  getSelectedUserIndex,
  isUserSelectDisabled,
  getDisabledUserMessage,
} from './config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    marginTop: spacing(6),
  },
  title: {
    marginBottom: spacing(4),
  },
  searchWrapper: {
    display: 'flex',
    alignItems: 'center',
    margin: spacing(4, 0),
  },
  clearAllButton: {
    backgroundColor: primary.white,
    border: `1px solid ${primary.bluish1}`,
    color: primary.bluish1,
    borderRadius: 44,
    fontFamily: 'ProximaNova-Bold',
    fontSize: 16,
    lineHeight: '22px',
    padding: spacing(2, 4),
    marginLeft: spacing(2),
    maxHeight: 40,
  },
  search: {
    width: 300,
  },
  usersWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  usersViewport: {
    maxHeight: 182,
  },
  users: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: `${spacing(4)}px ${spacing(2)}px`,
    width: 'calc(100% - 16px)',
  },
  user: {
    border: `1px solid ${primary.bluish6}`,
    borderRadius: 60,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: spacing(2, 4),
    transition: 'background-color .3s ease',
  },
  userNotClearable: {
    cursor: 'default',
  },
  selectedUser: {
    backgroundColor: primary.blue6,
  },
  disabledUser: {
    cursor: 'not-allowed',
    backgroundColor: primary.bluish7,
  },
  disabledTooltip: {
    cursor: 'not-allowed',
  },
  userLabel: {
    fontFamily: 'ProximaNova-Bold',
    whiteSpace: 'nowrap',
    userSelect: 'none',
  },
  removeIcon: {
    flexShrink: 0,
    height: 14,
    width: 14,
    marginLeft: spacing(3),
  },
  scrollY: {
    right: 8,
  },
  toggleSwitchWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',

    width: '100%',
  },
  switch: {
    marginTop: spacing(6),
  },
}));

const UsersFilter = ({
  translations,
  title,
  users,
  selectedUsers = [],
  searchTerm = '',
  activeColors = [],
  maxSelectedUsers = 0,
  shouldEnableOnlyFinishedUsers = false,
  isTeamReview,
  isSurveyCompleted,
  isUserClearable = false,
  isAverageView = false,
  hasFilter,
  hasSearch = false,
  hasAddAll = false,
  hasReportChart = false,
  hasAverageChart = false,
  handleChangeView = () => {},
  onSelectAll = () => {},
  onClear,
  onSearch,
  onSelect,
}) => {
  const classes = useStyles();
  const switchItems = getToggleReportViewItems(translations, true);

  return (
    <div className={classes.root}>
      {hasFilter && (
        <>
          <Typography
            className={classNames({ [classes.title]: !hasSearch })}
            variant="body2"
          >
            {title}
          </Typography>
          {hasSearch && (
            <div className={classes.searchWrapper}>
              <Search
                className={classes.search}
                placeholder={translations.search}
                value={searchTerm}
                onChange={onSearch}
              />
              <Fade in={hasAddAll} unmountOnExit>
                <CustomButton
                  className={classes.clearAllButton}
                  type="addWithTextRounded"
                  onClick={onSelectAll}
                >
                  {translations.addAllButtonLabel}
                </CustomButton>
              </Fade>
              <Fade in={!isArrayEmpty(selectedUsers)} unmountOnExit>
                <CustomButton
                  className={classes.clearAllButton}
                  type="addWithTextRounded"
                  onClick={onClear}
                >
                  {translations.clearAllButtonLabel}
                </CustomButton>
              </Fade>
            </div>
          )}
        </>
      )}
      <div className={classes.usersWrapper}>
        <Scrollbars
          viewportClass={classes.usersViewport}
          verticalScrollClass={classes.scrollY}
        >
          <div className={classes.chartHeaderGroup}>
            <div className={classes.users}>
              {hasFilter &&
                users.map(user => {
                  const isSelected = selectedUsers.includes(user.id);
                  const selectedUserIndex = getSelectedUserIndex(
                    user.id,
                    selectedUsers
                  );
                  const isDisabled = isUserSelectDisabled(
                    user,
                    selectedUsers,
                    maxSelectedUsers,
                    isSelected,
                    shouldEnableOnlyFinishedUsers
                  );

                  return (
                    <div key={`user_${user.id}`}>
                      <ConditionalTooltip
                        className={classNames({
                          [classes.disabledTooltip]: isDisabled,
                        })}
                        addTooltip={isDisabled}
                        message={getDisabledUserMessage(
                          translations,
                          user,
                          isTeamReview,
                          isSurveyCompleted
                        )}
                      >
                        <div
                          style={{
                            backgroundColor:
                              isSelected && selectedUserIndex !== -1
                                ? activeColors[selectedUserIndex]
                                : undefined,
                          }}
                          className={classNames(classes.user, {
                            [classes.selectedUser]: isSelected,
                            [classes.disabledUser]: isDisabled,
                            [classes.userNotClearable]: !isUserClearable,
                          })}
                          onClick={() =>
                            !isDisabled &&
                            isUserClearable &&
                            onSelect(user.id, isSelected)
                          }
                        >
                          <UserAvatar
                            labelClass={classes.userLabel}
                            getCustomCaption={getPersonShortName}
                            user={user}
                            caption
                          />
                          <Fade
                            in={isSelected && isUserClearable}
                            unmountOnExit
                          >
                            <RemoveIcon className={classes.removeIcon} />
                          </Fade>
                        </div>
                      </ConditionalTooltip>
                    </div>
                  );
                })}
            </div>
          </div>
        </Scrollbars>
        {hasReportChart && hasAverageChart && (
          <div
            className={classNames(classes.toggleSwitchWrapper, {
              [classes.switch]: hasFilter,
            })}
          >
            <ToggleButtonSwitch
              value={isAverageView}
              items={switchItems}
              onSelect={handleChangeView}
              isFullWidth
            />
          </div>
        )}
      </div>
    </div>
  );
};

UsersFilter.propTypes = {
  translations: PropTypes.object.isRequired,
  hasReportChart: PropTypes.bool,
  hasAverageChart: PropTypes.bool,
  hasFilter: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  users: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  searchTerm: PropTypes.string,
  selectedUsers: PropTypes.arrayOf(PropTypes.number),
  isAverageView: PropTypes.bool,
  activeColors: PropTypes.arrayOf(PropTypes.string),
  maxSelectedUsers: PropTypes.number,
  shouldEnableOnlyFinishedUsers: PropTypes.bool,
  isTeamReview: PropTypes.bool.isRequired,
  isSurveyCompleted: PropTypes.bool.isRequired,
  hasSearch: PropTypes.bool,
  isUserClearable: PropTypes.bool,
  hasAddAll: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func,
  onSearch: PropTypes.func.isRequired,
  handleChangeView: PropTypes.func,
};

export default UsersFilter;
